import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from "../icons/Icon";
import { format } from 'date-fns';
import { parsePeriodToDate } from '../../utils/periodDate';
import { calculateEmployeeFinancials } from '../../utils/payrollCalculations';
import { formatMonthLabel } from '../../utils/payslipBuilders';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';
import PwPopover from '../PwPopover';
import { SSS_BASIS_FACTORS } from '../../constants/sssBasisFactors';
import { round2 } from '../../services/contributionTableClient';
import {
  longMonthLabel,
  suppressionReason,
  boundNote,
  contributionsStartLabel,
  isBeforeContributionsStart,
} from '../../constants/basisNotes';

/**
 * PRD_SssContributionBasis C22 — the suggested SSS line beneath the balance chip,
 * with the derivation popover. Renders from the employee's own sssBasisResult
 * (C8c): every ticked factor rides in `components` (zeros included, for policy
 * traceability); the popover drops the zeros when it draws. Factor labels come
 * from the same source the settings card renders, so they match exactly.
 * `strategy="fixed"` — this lives in a scrollable table cell.
 */
// PRD_SssBasisPieceRateAndSuppressionReasons P6/P7 — one sentence per cause, now
// shared with the Contribution Basis report (PRD_ContributionBasisReport C17).
//
// P7 revised at /test-impl (2026-09-25): the sentence lives in a click popover
// beside `Suggested: ₱0.00` rather than inline, so a suppressed row reads the
// same shape as a live one. It deliberately does NOT reuse the derivation
// popover's C20 footer ("Contribution … · derived from recorded attendance at
// current rates") — with `amount` null that renders "Contribution ₱0.00" under
// a sentence saying there is no contribution, which is why the original review
// cut the popover rather than build it.

// `deducted` is what approved earlier runs already filed under the applicable month
// (getBalance's ledger side). C27: the line shows what is LEFT, not the whole month —
// a production double deduction came from `Suggested: ₱315.00` sitting under
// `Bal: ₱65.00`. The popover keeps the full contribution (C20 footer) and shows the working.
// PRD_ContributionsStartDate C7: a saved-amount cell before the contributions start
// month has a ₱0 target and no suggestion line, so this label says why — behind an
// ⓘ, the same shape as a suppressed basis line beside it.
const ContributionsStartMarker = ({ employment, monthKey, agency }) =>
  isBeforeContributionsStart(employment, monthKey) ? (
    <span className="deduction-suggestion" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.75rem', color: 'var(--ui-text-secondary)' }}>
      Not deducted
      <PwPopover
        ariaLabel={`Why ${agency} is not deducted`}
        align="start"
        strategy="fixed"
        trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
      >
        <div style={{ minWidth: 240, fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--ui-text-secondary)' }}>
          <strong style={{ display: 'block', marginBottom: 4, color: 'var(--ui-text)' }}>
            {agency} — {longMonthLabel(monthKey)}
          </strong>
          {contributionsStartLabel(employment.contributionsStartDate)}.
        </div>
      </PwPopover>
    </span>
  ) : null;

const BasisSuggestion = ({ result, formatCurrency, agency, deducted = 0 }) => {
  const month = longMonthLabel(result.applicableMonth);
  if (result.amount === null || result.amount === undefined) {
    const reason = suppressionReason(
      result.suppressedBy, result.applicableMonth, agency, result.contributionsStartDate
    );
    return (
      <span className="deduction-suggestion" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.75rem', color: 'var(--ui-text-secondary)' }}>
        Suggested: {formatCurrency(0)}
        <PwPopover
          ariaLabel={`Why the suggested ${agency} is zero`}
          align="start"
          strategy="fixed"
          trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
        >
          <div style={{ minWidth: 240, fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--ui-text-secondary)' }}>
            <strong style={{ display: 'block', marginBottom: 4, color: 'var(--ui-text)' }}>
              {agency} basis — {month}
            </strong>
            {reason}
          </div>
        </PwPopover>
      </span>
    );
  }
  const contribution = parseFloat(result.amount) || 0;
  const remaining = round2(Math.max(0, contribution - deducted));
  const overDeducted = round2(deducted - contribution);
  const rows = (result.components || []).filter((c) => c.amount !== 0);
  const labelOf = (key) => SSS_BASIS_FACTORS.find((f) => f.key === key)?.label || key;
  let firstAdd = true;
  return (
    <span className="deduction-suggestion" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, fontSize: '0.75rem', color: 'var(--ui-text-secondary)' }}>
      Suggested: {formatCurrency(remaining)}
      <PwPopover
        ariaLabel={`How the suggested ${agency} is derived`}
        align="start"
        strategy="fixed"
        trigger={<span aria-hidden="true" style={{ fontWeight: 700 }}>ⓘ</span>}
      >
        <div style={{ minWidth: 240, fontSize: '0.8125rem', lineHeight: 1.5, color: 'var(--ui-text-secondary)' }}>
          <strong style={{ display: 'block', marginBottom: 4, color: 'var(--ui-text)' }}>
            {agency} basis — {month}
          </strong>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {rows.map((c) => {
                let sign = '';
                if (c.sign === 'subtract') sign = '−';
                else if (!firstAdd) sign = '+';
                if (c.sign === 'add') firstAdd = false;
                return (
                  <tr key={c.factor}>
                    <td style={{ padding: '1px 8px 1px 0' }}>{labelOf(c.factor)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {sign}{formatCurrency(c.amount)}
                    </td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: '1px solid var(--ui-border)' }}>
                <td style={{ padding: '3px 8px 0 0' }}><strong>Basis</strong></td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', paddingTop: 3 }}>
                  <strong>{formatCurrency(result.basis)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
          {/* C9/H12: the floor/ceiling line — wording in constants/basisNotes.js. */}
          {boundNote(result.atBound, agency) && (
            <p style={{ margin: '6px 0 0' }}>{boundNote(result.atBound, agency)}</p>
          )}
          {/* C20: re-priced at today's rates, never "what you paid". Locked footer. */}
          <p style={{ margin: '6px 0 0', color: 'var(--ui-text-muted)' }}>
            Contribution {formatCurrency(result.amount)} · derived from recorded attendance at current rates
          </p>
          {deducted > 0 && (
            <>
              <p style={{ margin: '6px 0 0' }}>Already deducted in earlier runs: {formatCurrency(deducted)}</p>
              <p style={{ margin: '2px 0 0', color: 'var(--ui-text)' }}>
                <strong>Suggested this run: {formatCurrency(remaining)}</strong>
              </p>
              {overDeducted > 0 && (
                <p style={{ margin: '6px 0 0', color: 'var(--ui-warning-text)' }}>
                  {formatCurrency(overDeducted)} more than the contribution has been deducted.
                </p>
              )}
            </>
          )}
        </div>
      </PwPopover>
    </span>
  );
};

// C28: a typed amount that would take the applicable month past its composed
// contribution. Warning only — no clamp — so a deliberate catch-up stays possible.
// Composed lines only: a suppressed line (amount null) already explains itself, and
// basis-off targets are stored figures that are often blank by design (Tax).
// Triggers on what THIS entry adds past the remaining figure, never on the ledger
// alone: a month already over-deducted (a basis switch re-priced below last month's
// flat figure) must not paint a warning under a blank box nobody can clear — the
// popover says that one. The figure shown is the month's total overage.
const OverDeductionWarning = ({ result, balance, typed, formatCurrency }) => {
  if (result.amount === null || result.amount === undefined) return null;
  const entered = parseFloat(typed) || 0;
  if (round2(entered - Math.max(0, balance.contribution - balance.deducted)) <= 0) return null;
  const over = round2(entered + balance.deducted - balance.contribution);
  return (
    <span className="deduction-over-warning" style={{ display: 'block', marginTop: 4, fontSize: '0.75rem', color: 'var(--ui-warning-text)' }}>
      {`Over the ${longMonthLabel(result.applicableMonth)} contribution by ${formatCurrency(over)}`}
    </span>
  );
};

/**
 * DeductionsStep - Step 4 of Payroll Process
 * Interactive deductions management with:
 * - Bulk selection of deduction types (SSS, PhilHealth, Pag-IBIG, Tax)
 * - Cross-month support with dual columns
 * - Balance tracking per month
 * - Gross income validation
 * - PRD_SssContributionBasis: with the company's SSS basis on, the SSS row is a
 *   SINGLE column keyed by the applicable month (C18) whose target is the
 *   server-composed suggestion (C17). Every site — header, cells, clears, the
 *   suggestion — reads the employee's own sssBasisResult; no company-level prop.
 */
const DeductionsStep = ({
  employees,
  periodStart,
  periodEnd,
  onDeductionUpdate
}) => {
  // Sorting state
  const [sortField, setSortField] = useState('employeeName');
  const [sortDirection, setSortDirection] = useState('asc');

  // Bulk selection state
  // Default: SSS, PhilHealth, Pag-IBIG checked; Tax unchecked (most daily-rate workers don't meet tax threshold)
  const [selectedDeductions, setSelectedDeductions] = useState({
    sss: true,
    philhealth: true,
    pagibig: true,
    tax: false
  });

  // Employee deduction amounts state
  const [employeeDeductions, setEmployeeDeductions] = useState({});

  // Search & filter state (display only - does not affect parent data)
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Detect if period spans multiple months
  const periodMonths = useMemo(() => {
    if (!periodStart || !periodEnd) return [];

    const start = new Date(periodStart);
    const end = new Date(periodEnd);
    const months = [];

    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    while (current <= end) {
      months.push({
        key: `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`,
        label: format(current, 'MMM yyyy'),
        shortLabel: format(current, 'MMM')
      });
      current.setMonth(current.getMonth() + 1);
    }

    return months;
  }, [periodStart, periodEnd]);

  const isCrossMonth = periodMonths.length > 1;

  // PRD_SssContributionBasis C11a/C18: the SSS row's month list for ONE employee. Read
  // off that employee's own projection line — the server emits
  // sssBasisResult.applicableMonth for every line in a basis-on run (a suppressed
  // employee included), while a pre-flip line on a resumed draft carries none and
  // keeps period-month keying. Never re-derived on the client: periodDate.js has no
  // month-before helper, and a second definition of C7 would drift from readers.js.
  //
  // PRD_PhilHealthContributionBasis H12 — THE GOVERNING RULE: an agency's
  // regime is read from its OWN *BasisResult, never the other's. Every helper
  // below therefore takes the deduction `type` and has no default: a caller
  // cannot accidentally ask "is the basis on?" without saying whose.
  //
  // The failure this prevents is not forgetting PhilHealth — it is sweeping or
  // re-keying PhilHealth using SSS's applicable month, which for a company
  // running SSS-only would zero a legitimate PhilHealth deduction.
  const basisResultFor = (employee, type) => {
    if (type === 'sss') return employee?.sssBasisResult || null;
    if (type === 'philhealth') return employee?.philhealthBasisResult || null;
    return null; // pagibig and tax never compose — always per-period months
  };
  const applicableMonthFor = (employee, type) =>
    basisResultFor(employee, type)?.applicableMonth || null;

  const monthsFor = (employee, type) => {
    const am = applicableMonthFor(employee, type);
    if (!am) return periodMonths;
    return [{ key: am, label: formatMonthLabel(am), shortLabel: formatMonthLabel(am) }];
  };
  // Every key the row may hold under EITHER regime — what the clear paths cover.
  const keysFor = (employee, type) => {
    const keys = new Set(periodMonths.map((m) => m.key));
    const am = applicableMonthFor(employee, type);
    if (am) keys.add(am);
    return [...keys];
  };
  // The column header's single label — read off the lines like every other site,
  // so the header can never disagree with the cells beneath it.
  const headerMonths = useMemo(() => {
    const first = (type) => {
      const line = (employees || []).find((e) => (
        type === 'sss' ? e?.sssBasisResult?.applicableMonth : e?.philhealthBasisResult?.applicableMonth
      ));
      if (!line) return null;
      return type === 'sss' ?
        line.sssBasisResult.applicableMonth :
        line.philhealthBasisResult.applicableMonth;
    };
    return { sss: first('sss'), philhealth: first('philhealth') };
  }, [employees]);
  const sssHeaderMonth = headerMonths.sss;
  const philhealthHeaderMonth = headerMonths.philhealth;

  // C25: onDeductionUpdate is a fresh function on every parent render. Hold it in a
  // ref so the restore effect can call it without listing it as a dependency (the
  // PayrollSummary onTotalsCalculatedRef pattern).
  const onDeductionUpdateRef = useRef(onDeductionUpdate);
  onDeductionUpdateRef.current = onDeductionUpdate;

  // Initialize employee deductions when employees change
  // Reads from employee.deductions (per-month structure) to restore state on back navigation
  // DTR data is already available on employee.monthlyDtrData from Step 2
  useEffect(() => {
    if (!employees || employees.length === 0 || periodMonths.length === 0) {
      return;
    }

    // Helper to extract month value from per-month structure
    const getMonthValue = (deductionObj, monthKey) => {
      if (typeof deductionObj === 'object' && deductionObj !== null && !Array.isArray(deductionObj)) {
        return deductionObj[monthKey] || 0;
      }
      return 0;
    };

    // PRD_SssContributionBasis C25 — clear stale-regime SSS keys. This is the first
    // time an SSS key can be a month OUTSIDE the run's period, so one map can hold keys
    // from both regimes and nothing else removes one (the clear handlers walk one
    // regime; the recalc preserve block copies the map wholesale). The regime is read
    // from the employee's OWN sssBasisResult, never the prop: it comes from the server
    // compute, so a failed client settings read cannot make this zero the wrong keys —
    // and a pre-flip line (no record) is correctly treated as basis-off.
    //
    // Runs in the effect BODY, not inside the updater below: React forbids setting a
    // parent's state from inside another component's updater, and StrictMode
    // double-invokes updaters. The `amount > 0` guard is what makes it TERMINATE —
    // `employees` is rebuilt on every parent render, so each emit re-runs this effect;
    // the second pass finds the key already 0 and emits nothing.
    // H12: PER AGENCY, each off its OWN result. Sweeping PhilHealth's keys with
    // SSS's applicable month would zero a legitimate PhilHealth deduction for a
    // company running SSS-only — a silently dropped contribution.
    employees.forEach((emp) => {
      const employeeId = emp.employeeId || emp.id;
      ['sss', 'philhealth'].forEach((type) => {
        const map = emp.deductions?.[type];
        if (typeof map !== 'object' || map === null || Array.isArray(map)) return;
        const am = applicableMonthFor(emp, type);
        const live = am ? new Set([am]) : new Set(periodMonths.map((m) => m.key));
        Object.entries(map).forEach(([key, amount]) => {
          if (!live.has(key) && amount > 0 && onDeductionUpdateRef.current) {
            onDeductionUpdateRef.current(employeeId, key, type, 0);
          }
        });
      });
    });

    // Initialize deductions from employee.deductions (per-month structure)
    // This restores previously entered values when navigating back
    setEmployeeDeductions(prev => {
      const updated = { ...prev };

      employees.forEach(emp => {
        const employeeId = emp.employeeId || emp.id;

        // Only initialize if not already in local state
        if (!updated[employeeId]) {
          updated[employeeId] = {};
          const amSss = applicableMonthFor(emp, 'sss');
          const amPh = applicableMonthFor(emp, 'philhealth');

          periodMonths.forEach(month => {
            // Read from employee.deductions per-month structure if available
            // Structure: employee.deductions.sss = { '2025-12': 300, '2026-01': 300 }
            // C18: a basis-on line's SSS lives under the applicable month, so its
            // period-month SSS slots seed 0 — a stale period key must not leak into
            // the displayed total while C25 is clearing it on the parent.
            updated[employeeId][month.key] = {
              sss: amSss ? 0 : getMonthValue(emp.deductions?.sss, month.key),
              // H12: the same rule for PhilHealth, off ITS own result — a
              // composing PhilHealth's period-month slots seed 0 so a stale
              // period key cannot leak into the displayed total while the sweep
              // clears it on the parent.
              philhealth: amPh ? 0 : getMonthValue(emp.deductions?.philhealth, month.key),
              pagibig: getMonthValue(emp.deductions?.pagibig, month.key),
              tax: getMonthValue(emp.deductions?.tax, month.key)
            };
          });
          // C18 + H12 (sites 5/9): the applicable month is never a period month, so its
          // entry is seeded too. BOTH agencies resolve to the SAME month, so
          // they SHARE this entry — which is why each agency's value is written
          // independently and NOT guarded on whether the entry already exists.
          // Guarding per agency (the obvious copy-paste) makes whichever runs
          // second find the entry present and skip, hiding a saved amount on
          // resume while it stays in the parent and reaches the DTR.
          const amShared = amSss || amPh;
          if (amShared) {
            const existing = updated[employeeId][amShared] ||
              { sss: 0, philhealth: 0, pagibig: 0, tax: 0 };
            updated[employeeId][amShared] = {
              ...existing,
              sss: amSss ? getMonthValue(emp.deductions?.sss, amSss) : existing.sss,
              philhealth: amPh ? getMonthValue(emp.deductions?.philhealth, amPh) : existing.philhealth,
            };
          }
        }
      });

      return updated;
    });
  }, [employees, periodMonths]);

  // Get DTR data for an employee from the passed props (already fetched in Step 2)
  const getDtrData = (employeeId, monthKey) => {
    const employee = employees.find(emp => (emp.employeeId || emp.id) === employeeId);
    if (!employee || !employee.monthlyDtrData) {
      return {
        sssDeducted: 0,
        philhealthDeducted: 0,
        pagibigDeducted: 0,
        withholdingTaxDeducted: 0
      };
    }
    return employee.monthlyDtrData[monthKey] || {
      sssDeducted: 0,
      philhealthDeducted: 0,
      pagibigDeducted: 0,
      withholdingTaxDeducted: 0
    };
  };

  // Calculate balance for a contribution type. The monthly TARGET is sourced live
  // from the employee record (employment.*) — not the DTR snapshot — so it reflects
  // the current contribution config; the DEDUCTED side still comes per-month from
  // monthlyDtrData (C2).
  const getBalance = (employeeId, monthKey, type) => {
    const dtr = getDtrData(employeeId, monthKey);
    const employee = employees.find(emp => (emp.employeeId || emp.id) === employeeId);
    const employment = employee?.employment || {};
    const num = (v) => parseFloat(v) || 0;

    // PRD_SssContributionBasis C17: with the basis on, the SSS target IS the composed
    // suggestion — never the stored employment.sssContribution — so balance ≤ suggested
    // always holds. A suppressed employee (amount null — any suppressedBy cause) has a
    // target of 0 and a depleted chip that fills nothing. `monthKey` is the applicable
    // month here (the caller passes monthsFor(emp, type)), so the deducted side and the write
    // are keyed alike (C8a) and getDtrData needs no change.
    // H12: whichever agency composes, the composed amount IS the target — never
    // the stored employment.* figure. Keyed on that agency's OWN result.
    const composed = basisResultFor(employee, type);
    if (composed?.applicableMonth) {
      const amount = composed.amount;
      const contribution = amount === null || amount === undefined ? 0 : num(amount);
      const deducted = type === 'sss' ? dtr.sssDeducted : dtr.philhealthDeducted;
      return {
        contribution,
        deducted,
        remaining: Math.max(0, contribution - deducted)
      };
    }

    const contributionMap = {
      sss: { contribution: num(employment.sssContribution), deducted: dtr.sssDeducted },
      philhealth: { contribution: num(employment.philhealthContribution), deducted: dtr.philhealthDeducted },
      pagibig: { contribution: num(employment.pagibigContribution), deducted: dtr.pagibigDeducted },
      tax: { contribution: num(employment.withholdingTax), deducted: dtr.withholdingTaxDeducted }
    };

    const data = contributionMap[type] || { contribution: 0, deducted: 0 };
    // PRD_ContributionsStartDate C5: no saved government contribution is due in a
    // month before the contributions start month. Withholding tax is untouched.
    if (type !== 'tax' && isBeforeContributionsStart(employment, monthKey)) data.contribution = 0;
    return {
      contribution: data.contribution,
      deducted: data.deducted,
      // PRD_ContributionsStartDate C7: clamped as the composed branch is, so an
      // over-deducted month never fills a negative deduction.
      remaining: Math.max(0, data.contribution - data.deducted)
    };
  };

  // Handle deduction amount change
  const handleDeductionChange = (employeeId, monthKey, type, value) => {
    const numValue = Math.round((parseFloat(value) || 0) * 100) / 100;

    setEmployeeDeductions(prev => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [monthKey]: {
          ...prev[employeeId]?.[monthKey],
          [type]: numValue
        }
      }
    }));

    // Notify parent of update
    if (onDeductionUpdate) {
      onDeductionUpdate(employeeId, monthKey, type, numValue);
    }
  };

  // Handle deduction type toggle (checkbox)
  // When unchecked, clear all values for that deduction type and notify parent
  const handleDeductionTypeToggle = (type, isChecked) => {
    setSelectedDeductions(prev => ({ ...prev, [type]: isChecked }));

    // If unchecked, clear all values for this deduction type
    if (!isChecked) {
      const newDeductions = { ...employeeDeductions };

      employees.forEach(emp => {
        const employeeId = emp.employeeId || emp.id;
        if (newDeductions[employeeId]) {
          // C18: the SSS row may be keyed by the applicable month — never a period
          // month — and a stale key from the other regime may exist; clear them ALL,
          // or "I unticked SSS and it still deducted".
          const keys = keysFor(emp, type);
          keys.forEach(key => {
            if (newDeductions[employeeId][key]) {
              newDeductions[employeeId][key][type] = 0;
            }
            // Notify parent to clear this value
            if (onDeductionUpdate) {
              onDeductionUpdate(employeeId, key, type, 0);
            }
          });
        }
      });

      setEmployeeDeductions(newDeductions);
    }
  };

  // Apply to all employees.
  // C3 (PRD_MonthlySalaryType): a cross-month run belongs to its START month —
  // bulk-apply defaults government deductions into that attribution month only
  // (filling every month would also take the NEXT month's contribution in this
  // run). The other month's column stays editable for deliberate overrides.
  const handleApplyToAll = () => {
    const newDeductions = { ...employeeDeductions };
    const attributionKey = periodMonths[0]?.key;

    employees.forEach(emp => {
      const employeeId = emp.employeeId || emp.id;
      if (!newDeductions[employeeId]) {
        newDeductions[employeeId] = {};
      }

      ['sss', 'philhealth', 'pagibig', 'tax'].forEach(type => {
        if (!selectedDeductions[type]) return;
        // C18: a basis-on line's SSS is a single applicable-month column — fill it.
        // For everything else this is the period-month walk it always was.
        const months = monthsFor(emp, type);
        months.forEach(month => {
          if (!newDeductions[employeeId][month.key]) {
            newDeductions[employeeId][month.key] = { sss: 0, philhealth: 0, pagibig: 0, tax: 0 };
          }
          const fill = (months.length === 1 || month.key === attributionKey)
            ? getBalance(employeeId, month.key, type).remaining
            : 0;
          newDeductions[employeeId][month.key][type] = fill;
          // Notify parent of update
          if (onDeductionUpdate) {
            onDeductionUpdate(employeeId, month.key, type, fill);
          }
        });
      });
    });

    setEmployeeDeductions(newDeductions);
  };

  // Clear all deductions
  const handleClearAll = () => {
    const newDeductions = {};
    employees.forEach(emp => {
      const employeeId = emp.employeeId || emp.id;
      newDeductions[employeeId] = {};
      periodMonths.forEach(month => {
        newDeductions[employeeId][month.key] = { sss: 0, philhealth: 0, pagibig: 0, tax: 0 };
        // Notify parent to clear all values
        ['sss', 'philhealth', 'pagibig', 'tax'].forEach(type => {
          if (onDeductionUpdate) {
            onDeductionUpdate(employeeId, month.key, type, 0);
          }
        });
      });
      // C18: a basis-on line's SSS key is the applicable month, never a period month —
      // clear it too, or Clear All leaves the deduction in place.
      // H12 (sites 5/9 guard note): both agencies share this entry, so each is
      // cleared and EMITTED independently, never guarded on the entry existing.
      // The shipped SSS-only guard was harmless — with one agency the entry was
      // always absent. A per-agency COPY of it is what would break: whichever
      // ran second would find the entry present and skip, blanking the screen
      // while the amount survived in the parent and reached the DTR.
      const amSss = applicableMonthFor(emp, 'sss');
      const amPh = applicableMonthFor(emp, 'philhealth');
      const amShared = amSss || amPh;
      if (amShared) {
        newDeductions[employeeId][amShared] = {
          ...(newDeductions[employeeId][amShared] || {}),
          sss: 0, philhealth: 0, pagibig: 0, tax: 0,
        };
        if (onDeductionUpdate) {
          if (amSss) onDeductionUpdate(employeeId, amSss, 'sss', 0);
          if (amPh) onDeductionUpdate(employeeId, amPh, 'philhealth', 0);
        }
      }
    });
    setEmployeeDeductions(newDeductions);
  };

  // Calculate employee total deduction
  const getEmployeeTotalDeduction = (employeeId) => {
    const deductions = employeeDeductions[employeeId];
    if (!deductions) return 0;

    let total = 0;
    Object.values(deductions).forEach(monthData => {
      ['sss', 'philhealth', 'pagibig', 'tax'].forEach(type => {
        if (selectedDeductions[type]) {
          total += monthData[type] || 0;
        }
      });
    });
    return total;
  };

  // Get gross income for employee using shared utility
  const getEmployeeGross = (employee) => {
    return calculateEmployeeFinancials(employee).gross;
  };

  // Calculate deduction percentage of gross
  const getDeductionPercentage = (employeeId, employee) => {
    const gross = getEmployeeGross(employee);
    if (gross === 0) return 0;
    const totalDeduction = getEmployeeTotalDeduction(employeeId);
    return (totalDeduction / gross) * 100;
  };

  // Get percentage color class
  const getPercentageColor = (percentage) => {
    if (percentage > 100) return 'red';
    if (percentage > 60) return 'red';
    if (percentage > 30) return 'yellow';
    return 'green';
  };

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    let totalSSS = 0;
    let totalPhilhealth = 0;
    let totalPagibig = 0;
    let totalTax = 0;

    employees.forEach(emp => {
      const employeeId = emp.employeeId || emp.id;
      const deductions = employeeDeductions[employeeId];
      if (!deductions) return;

      Object.values(deductions).forEach(monthData => {
        if (selectedDeductions.sss) totalSSS += monthData.sss || 0;
        if (selectedDeductions.philhealth) totalPhilhealth += monthData.philhealth || 0;
        if (selectedDeductions.pagibig) totalPagibig += monthData.pagibig || 0;
        if (selectedDeductions.tax) totalTax += monthData.tax || 0;
      });
    });

    return {
      totalSSS,
      totalPhilhealth,
      totalPagibig,
      totalTax,
      grandTotal: totalSSS + totalPhilhealth + totalPagibig + totalTax
    };
  }, [employees, employeeDeductions, selectedDeductions]);

  // Get employee initials (FirstName initial + LastName initial)
  const getInitials = (employee) => {
    const first = employee.personalInfo?.firstName?.charAt(0) || '';
    const last = employee.personalInfo?.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || '??';
  };

  // Get employee full name - Format: "Last Name, First Name" for consistent sorting
  const getFullName = (employee) => {
    const firstName = employee.personalInfo?.firstName || '';
    const lastName = employee.personalInfo?.lastName || '';
    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }
    return employee.personalInfo?.fullName || lastName || firstName || 'Unknown';
  };

  // Get employee department
  const getEmployeeDepartment = (employee) => {
    return employee.employment?.department || employee.department || '';
  };

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort Icon Component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  // Sort employees
  const sortedEmployees = useMemo(() => {
    if (!employees || employees.length === 0) return [];

    return [...employees].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          // Sort by lastName first, then firstName as secondary sort
          aValue = (a.personalInfo?.lastName || '').toLowerCase();
          bValue = (b.personalInfo?.lastName || '').toLowerCase();
          if (aValue === bValue) {
            aValue = (a.personalInfo?.firstName || '').toLowerCase();
            bValue = (b.personalInfo?.firstName || '').toLowerCase();
          }
          break;
        case 'employeeId':
          aValue = a.employeeId || a.id || '';
          bValue = b.employeeId || b.id || '';
          break;
        default:
          aValue = (a.personalInfo?.lastName || '').toLowerCase();
          bValue = (b.personalInfo?.lastName || '').toLowerCase();
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
  }, [employees, sortField, sortDirection]);

  // Get unique departments for filter
  const departments = useMemo(() => {
    return [...new Set((employees || []).map(emp => getEmployeeDepartment(emp)))].filter(Boolean).sort();
  }, [employees]);

  // Filter sorted employees for display (search/filter is local only)
  const displayedEmployees = useMemo(() => {
    let result = sortedEmployees;
    if (selectedDepartment !== 'all') {
      result = result.filter(emp => getEmployeeDepartment(emp) === selectedDepartment);
    }
    if (searchTerm) {
      result = result.filter(emp => matchesEmployeeSearch(emp, searchTerm));
    }
    return result;
  }, [sortedEmployees, selectedDepartment, searchTerm]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!employees || employees.length === 0) {
    return (
      <div className="deductions-step">
        <div className="deductions-empty">
          <p>No employees found for this payroll period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deductions-step">
      {/* Bulk Selection Panel */}
      <div className="bulk-selection-panel">
        <div className="bulk-selection__grid">
          <span className="bulk-selection__title">Deduct this cycle:</span>

          <label className={`deduction-checkbox ${selectedDeductions.sss ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={selectedDeductions.sss}
              onChange={(e) => handleDeductionTypeToggle('sss', e.target.checked)}
            />
            <span className="deduction-checkbox__label">SSS</span>
          </label>

          <label className={`deduction-checkbox ${selectedDeductions.philhealth ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={selectedDeductions.philhealth}
              onChange={(e) => handleDeductionTypeToggle('philhealth', e.target.checked)}
            />
            <span className="deduction-checkbox__label">PhilHealth</span>
          </label>

          <label className={`deduction-checkbox ${selectedDeductions.pagibig ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={selectedDeductions.pagibig}
              onChange={(e) => handleDeductionTypeToggle('pagibig', e.target.checked)}
            />
            <span className="deduction-checkbox__label">Pag-IBIG</span>
          </label>

          <label className={`deduction-checkbox ${selectedDeductions.tax ? 'selected' : ''}`}>
            <input
              type="checkbox"
              checked={selectedDeductions.tax}
              onChange={(e) => handleDeductionTypeToggle('tax', e.target.checked)}
            />
            <span className="deduction-checkbox__label">Tax</span>
          </label>

          <div className="bulk-selection__actions">
            <button className="btn btn-primary" onClick={handleApplyToAll}>
              Apply to All
            </button>
            <button className="btn btn-secondary" onClick={handleClearAll}>
              Clear All
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="deductions-content">
        <div className="content-header">
          <div className="pw-header">
            <h2 className="pw-header__title">Employee Deductions</h2>
            <span className="pw-header__badge">
              {format(parsePeriodToDate(periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(periodEnd), 'MMM d, yyyy')}
            </span>
          </div>
          {isCrossMonth && (
            <span className="cross-month-badge">
              Cross-month period: Showing both {periodMonths.map(m => m.shortLabel).join(' and ')} columns
              {/* C18: with the basis on, "both columns" is false for SSS — say so.
                  Basis-off keeps today's wording verbatim. Locked string. */}
              {/* H12: the SSS-only form is VERBATIM (a test pins it exactly);
                  the other two are new. Both agencies share one applicable
                  month, so the both-on form names it once. */}
              {sssHeaderMonth && philhealthHeaderMonth
                ? ` — SSS and PhilHealth are single ${formatMonthLabel(sssHeaderMonth)} columns.`
                : sssHeaderMonth
                  ? ` — SSS is a single ${formatMonthLabel(sssHeaderMonth)} column.`
                  : philhealthHeaderMonth
                    ? ` — PhilHealth is a single ${formatMonthLabel(philhealthHeaderMonth)} column.`
                    : ''}
            </span>
          )}
        </div>

        {/* Search & Filter Controls */}
        <div className="pw-toolbar">
          <div className="pw-toolbar__controls">
            <input
              type="text"
              className="pw-toolbar__search"
              placeholder="Search by name or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="pw-toolbar__select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="all">All Departments</option>
              {departments.map(dept => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
          </div>
          <span className="pw-toolbar__count">
            <em>Showing {displayedEmployees.length} of {employees.length} employees</em>
          </span>
        </div>

        {/* Employee Table */}
        <div className="pw-table-scroll">
          <table className="pw-table pw-table--deductions">
            <thead>
              <tr>
                <th className="th-employee sortable-header" onClick={() => handleSort('employeeName')}>
                  Employee<SortIcon field="employeeName" />
                </th>
                <th className="th-id sortable-header" onClick={() => handleSort('employeeId')}>
                  ID<SortIcon field="employeeId" />
                </th>
                <th className="th-deduction">
                  SSS
                  {/* C18: with the basis on the SSS column carries the APPLICABLE
                      month's label, not one span per period month. */}
                  {sssHeaderMonth ? (
                    <div className="month-header">
                      <span>{formatMonthLabel(sssHeaderMonth)}</span>
                    </div>
                  ) : isCrossMonth && (
                    <div className="month-header">
                      {periodMonths.map(m => (
                        <span key={m.key}>{m.shortLabel}</span>
                      ))}
                    </div>
                  )}
                </th>
                <th className="th-deduction">
                  PhilHealth
                  {/* H12 site 10: mirrors the SSS header — one labelled column
                      when PhilHealth composes, the period months otherwise. */}
                  {philhealthHeaderMonth ? (
                    <div className="month-header">
                      <span>{formatMonthLabel(philhealthHeaderMonth)}</span>
                    </div>
                  ) : isCrossMonth && (
                    <div className="month-header">
                      {periodMonths.map(m => (
                        <span key={m.key}>{m.shortLabel}</span>
                      ))}
                    </div>
                  )}
                </th>
                <th className="th-deduction">
                  Pag-IBIG
                  {isCrossMonth && (
                    <div className="month-header">
                      {periodMonths.map(m => (
                        <span key={m.key}>{m.shortLabel}</span>
                      ))}
                    </div>
                  )}
                </th>
                <th className="th-deduction">
                  Tax
                  {isCrossMonth && (
                    <div className="month-header">
                      {periodMonths.map(m => (
                        <span key={m.key}>{m.shortLabel}</span>
                      ))}
                    </div>
                  )}
                </th>
                <th className="th-total">Total / Gross</th>
              </tr>
            </thead>
            <tbody>
              {displayedEmployees.map((employee) => {
                const employeeId = employee.employeeId || employee.id;
                const totalDeduction = getEmployeeTotalDeduction(employeeId);
                const grossIncome = getEmployeeGross(employee);
                const percentage = getDeductionPercentage(employeeId, employee);
                const percentColor = getPercentageColor(percentage);
                const exceedsGross = totalDeduction > grossIncome;

                return (
                  <React.Fragment key={employeeId}>
                    {/* Main Employee Row */}
                    <tr className={`employee-row ${exceedsGross ? 'error-row' : ''}`}>
                      <td className="pw-name-cell">
                        <div className="pw-name-cell__inner">
                          <div className="pw-avatar">{getInitials(employee)}</div>
                          <span className="pw-name-cell__text">{getFullName(employee)}</span>
                        </div>
                      </td>
                      <td>
                        <span className="employee-id">{employeeId}</span>
                      </td>

                      {/* SSS Column — C18: monthsFor(employee, 'sss') is the single
                          applicable-month entry for a basis-on line, else the period
                          months as today. This is where C8a's month key is emitted. */}
                      <td className="td-deduction">
                        {selectedDeductions.sss ? (
                          <div className="deduction-inputs">
                            {monthsFor(employee, 'sss').map(month => {
                              const balance = getBalance(employeeId, month.key, 'sss');
                              return (
                                <div key={month.key} className="deduction-cell">
                                  <input
                                    type="number"
                                    className="deduction-input"
                                    value={employeeDeductions[employeeId]?.[month.key]?.sss || ''}
                                    onChange={(e) => handleDeductionChange(employeeId, month.key, 'sss', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                  />
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    className={`deduction-balance ${balance.remaining <= 0 ? 'deduction-balance--depleted' : ''}`}
                                    title={`Remaining ${formatCurrency(balance.remaining)} of ${formatCurrency(balance.contribution)} — click to fill`}
                                    onClick={() => handleDeductionChange(employeeId, month.key, 'sss', balance.remaining)}
                                  >
                                    Bal: {formatCurrency(balance.remaining)}
                                  </button>
                                  {/* C26 deferred: nothing pre-fills; the suggestion is its
                                      own line beneath the chip, popover attached (C22). */}
                                  {employee.sssBasisResult?.applicableMonth && (
                                    <>
                                      <BasisSuggestion result={employee.sssBasisResult} formatCurrency={formatCurrency} agency="SSS" deducted={balance.deducted} />
                                      <OverDeductionWarning result={employee.sssBasisResult} balance={balance} typed={employeeDeductions[employeeId]?.[month.key]?.sss} formatCurrency={formatCurrency} />
                                    </>
                                  )}
                                  {!employee.sssBasisResult?.applicableMonth && (
                                    <ContributionsStartMarker employment={employee.employment} monthKey={month.key} agency="SSS" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="deduction-disabled">--</span>
                        )}
                      </td>

                      {/* PhilHealth Column — H12 site 11. This loop was
                          hard-coded to periodMonths, which is why a grep for
                          'sss' could not find it: converting the SSS-side
                          helpers alone would leave this cell showing the
                          composed target against a period month's deducted
                          amount, writing to a period key, and then having the
                          stale-key sweep zero what the operator just typed. */}
                      <td className="td-deduction">
                        {selectedDeductions.philhealth ? (
                          <div className="deduction-inputs">
                            {monthsFor(employee, 'philhealth').map(month => {
                              const balance = getBalance(employeeId, month.key, 'philhealth');
                              return (
                                <div key={month.key} className="deduction-cell">
                                  <input
                                    type="number"
                                    className="deduction-input"
                                    value={employeeDeductions[employeeId]?.[month.key]?.philhealth || ''}
                                    onChange={(e) => handleDeductionChange(employeeId, month.key, 'philhealth', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                  />
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    className={`deduction-balance ${balance.remaining <= 0 ? 'deduction-balance--depleted' : ''}`}
                                    title={`Remaining ${formatCurrency(balance.remaining)} of ${formatCurrency(balance.contribution)} — click to fill`}
                                    onClick={() => handleDeductionChange(employeeId, month.key, 'philhealth', balance.remaining)}
                                  >
                                    Bal: {formatCurrency(balance.remaining)}
                                  </button>
                                  {/* H12 site 12: no suggestion line existed under
                                      this column. Mirrors the SSS one — the composed
                                      figure with its derivation popover, or the
                                      reason it is suppressed. */}
                                  {employee.philhealthBasisResult?.applicableMonth && (
                                    <>
                                      <BasisSuggestion result={employee.philhealthBasisResult} formatCurrency={formatCurrency} agency="PhilHealth" deducted={balance.deducted} />
                                      <OverDeductionWarning result={employee.philhealthBasisResult} balance={balance} typed={employeeDeductions[employeeId]?.[month.key]?.philhealth} formatCurrency={formatCurrency} />
                                    </>
                                  )}
                                  {!employee.philhealthBasisResult?.applicableMonth && (
                                    <ContributionsStartMarker employment={employee.employment} monthKey={month.key} agency="PhilHealth" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="deduction-disabled">--</span>
                        )}
                      </td>

                      {/* Pag-IBIG Column */}
                      <td className="td-deduction">
                        {selectedDeductions.pagibig ? (
                          <div className="deduction-inputs">
                            {periodMonths.map(month => {
                              const balance = getBalance(employeeId, month.key, 'pagibig');
                              return (
                                <div key={month.key} className="deduction-cell">
                                  <input
                                    type="number"
                                    className="deduction-input"
                                    value={employeeDeductions[employeeId]?.[month.key]?.pagibig || ''}
                                    onChange={(e) => handleDeductionChange(employeeId, month.key, 'pagibig', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                  />
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    className={`deduction-balance ${balance.remaining <= 0 ? 'deduction-balance--depleted' : ''}`}
                                    title={`Remaining ${formatCurrency(balance.remaining)} of ${formatCurrency(balance.contribution)} — click to fill`}
                                    onClick={() => handleDeductionChange(employeeId, month.key, 'pagibig', balance.remaining)}
                                  >
                                    Bal: {formatCurrency(balance.remaining)}
                                  </button>
                                  <ContributionsStartMarker employment={employee.employment} monthKey={month.key} agency="Pag-IBIG" />
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="deduction-disabled">--</span>
                        )}
                      </td>

                      {/* Tax Column */}
                      <td className="td-deduction">
                        {selectedDeductions.tax ? (
                          <div className="deduction-inputs">
                            {periodMonths.map(month => {
                              const balance = getBalance(employeeId, month.key, 'tax');
                              return (
                                <div key={month.key} className="deduction-cell">
                                  <input
                                    type="number"
                                    className="deduction-input"
                                    value={employeeDeductions[employeeId]?.[month.key]?.tax || ''}
                                    onChange={(e) => handleDeductionChange(employeeId, month.key, 'tax', e.target.value)}
                                    placeholder="0"
                                    min="0"
                                    step="0.01"
                                  />
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    className={`deduction-balance ${balance.remaining <= 0 ? 'deduction-balance--depleted' : ''}`}
                                    title={`Remaining ${formatCurrency(balance.remaining)} of ${formatCurrency(balance.contribution)} — click to fill`}
                                    onClick={() => handleDeductionChange(employeeId, month.key, 'tax', balance.remaining)}
                                  >
                                    Bal: {formatCurrency(balance.remaining)}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="deduction-disabled">--</span>
                        )}
                      </td>

                      {/* Total / Gross Column */}
                      <td className="td-total-gross">
                        <div className="total-gross-cell">
                          <div className={`total-gross-cell__total ${exceedsGross ? 'error' : ''}`}>
                            Total: {formatCurrency(totalDeduction)}
                            <span className={`percentage-badge percentage-badge--${percentColor}`}>
                              {Math.round(percentage)}%
                            </span>
                          </div>
                          <div className="total-gross-cell__bar">
                            <div
                              className={`total-gross-cell__bar-fill total-gross-cell__bar-fill--${percentColor}`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                          <div className="total-gross-cell__gross">
                            Gross: {formatCurrency(grossIncome)}
                          </div>
                          {exceedsGross && (
                            <div className="total-gross-cell__error">
                              EXCEEDS GROSS!
                            </div>
                          )}
                        </div>
                      </td>

                    </tr>

                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {displayedEmployees.length === 0 && (
            <div className="deductions-no-results">
              <p>No employees found matching your criteria</p>
            </div>
          )}
        </div>

        {/* Table Footer */}
        <div className="pw-footer">
            <div className="pw-footer__stats">
              <div className="pw-footer__stat">
                <span className="pw-footer__stat-label">Total Employees</span>
                <span className="pw-footer__stat-value">{employees.length}</span>
              </div>
              <div className="pw-footer__stat">
                <span className="pw-footer__stat-label">Total SSS</span>
                <span className="pw-footer__stat-value">{formatCurrency(summaryStats.totalSSS)}</span>
              </div>
              <div className="pw-footer__stat">
                <span className="pw-footer__stat-label">Total PhilHealth</span>
                <span className="pw-footer__stat-value">{formatCurrency(summaryStats.totalPhilhealth)}</span>
              </div>
              <div className="pw-footer__stat">
                <span className="pw-footer__stat-label">Total Pag-IBIG</span>
                <span className="pw-footer__stat-value">{formatCurrency(summaryStats.totalPagibig)}</span>
              </div>
              <div className="pw-footer__stat">
                <span className="pw-footer__stat-label">Total Tax</span>
                <span className="pw-footer__stat-value">{formatCurrency(summaryStats.totalTax)}</span>
              </div>
              <div className="pw-footer__stat pw-footer__stat--total">
                <span className="pw-footer__stat-label">Grand Total</span>
                <span className="pw-footer__stat-value">{formatCurrency(summaryStats.grandTotal)}</span>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
};

export default DeductionsStep;
